/**
 * TrpcPostalCodeLookup — the production `PostalCodeLookupPort` over the typed
 * tRPC client (034 US3, T032). Calls the live `member.profile.postalLookup`
 * procedure: the member's cookie session authenticates the same-origin request.
 *
 * The postal autofill is OPTIONAL and NON-BLOCKING (the contact form works
 * without it). This adapter therefore degrades to `{ city: null }` — never
 * throws — on an absent `member.profile` namespace OR any transport hiccup, so a
 * failed lookup leaves the member's typed value untouched rather than crashing
 * the form (the `PostalCodeLookupPort` crash-tolerant contract). The API already
 * maps an unknown code (IS 404) and any read-failure to `{ city: null }`, so the
 * only extra guard here is the same-origin request itself.
 */

import type { WebTrpcClient } from '../trpc/client.js';
import type { PostalCodeLookupPort, PostalCodeLookupResult } from './PostalCodeLookupPort.js';

export class TrpcPostalCodeLookup implements PostalCodeLookupPort {
  readonly #client: WebTrpcClient;

  constructor(client: WebTrpcClient) {
    this.#client = client;
  }

  async lookupCity(postalCode: string): Promise<PostalCodeLookupResult> {
    const profile = this.#client.member?.profile;
    if (profile === undefined) {
      // No namespace (IS not configured) → no autofill, not a crash: the postal
      // helper is optional by contract, so a composition without it degrades.
      return { city: null };
    }
    try {
      return await profile.postalLookup.query({ postalCode });
    } catch {
      // Same-origin transport hiccup → no autofill; the member keeps their value.
      return { city: null };
    }
  }
}
